import React from "react"
import WorkflowDiagram, { workflows } from "./workflow-diagram"
import { makeStyles } from "@material-ui/core/styles"

const useStyles = makeStyles(theme => ({
  figure: {
    margin: "28px 0",
    padding: 24,
    border: "1px solid #dbe3ee",
    borderRadius: 16,
    background: "#f7f9fd",
    color: "#233348",
    fontFamily: theme.typography.fontFamily,
    fontSize: 15,
    lineHeight: 1.6,
    "& p": { margin: "8px 0 0" },
    "& strong": { fontWeight: 600 },
    [theme.breakpoints.down("xs")]: { padding: 16 },
  },
  title: { fontSize: 19, fontWeight: 600, marginBottom: 18 },
  caption: { marginTop: 18, color: "#536276", fontSize: 14 },
  flow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    listStyle: "none",
    padding: 0,
    margin: 0,
    gap: 24,
    "& > li": { position: "relative", minWidth: 0 },
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr", gap: 28 },
  },
  step: {
    padding: 14,
    background: "#fff",
    border: "1px solid #dbe3ee",
    borderRadius: 10,
    height: "100%",
    boxSizing: "border-box",
  },
  arrow: {
    position: "absolute",
    right: -20,
    top: 18,
    color: "#345a96",
    [theme.breakpoints.down("xs")]: {
      right: "50%",
      top: "auto",
      bottom: -27,
      transform: "rotate(90deg)",
    },
  },
  columns: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 16,
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  panel: {
    padding: 16,
    background: "#fff",
    border: "1px solid #dbe3ee",
    borderRadius: 10,
    minWidth: 0,
  },
  badge: {
    display: "block",
    color: "#315d9e",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 6,
  },
  note: {
    marginTop: 16,
    padding: "12px 16px",
    borderLeft: "3px solid #638aca",
    background: "#edf3ff",
  },
  event: {
    borderLeft: "4px solid #638aca",
    background: "#f1f5fd",
    padding: "8px 12px",
    marginTop: 10,
  },
  week: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 6,
    marginTop: 12,
    fontSize: 13,
    textAlign: "center",
  },
  day: { padding: "10px 3px", background: "#edf3ff", borderRadius: 6 },
  notes: {
    border: "1px solid #bdcadb",
    borderRadius: 10,
    overflow: "hidden",
    background: "white",
  },
  notesTop: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(0, 3fr)",
  },
  cue: { padding: 14, background: "#edf3ff", borderRight: "1px solid #bdcadb" },
  notesBody: { padding: 14 },
  summary: {
    padding: 14,
    borderTop: "1px solid #bdcadb",
    background: "#f4f0ff",
  },
  tiles: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 },
  tile: {
    padding: "6px 10px",
    border: "1px solid #7393ba",
    borderRadius: 6,
    background: "#e9f2ff",
    fontWeight: 600,
  },
  secondTile: { background: "#fff1dc", borderColor: "#ae7f38" },
  svg: {
    display: "block",
    width: "100%",
    height: "auto",
    maxWidth: 280,
    margin: "12px auto 0",
  },
}))

export function DiagramFigure({
  id,
  title,
  caption,
  children,
}: {
  id: string
  title: string
  caption: string
  children: React.ReactNode
}) {
  const c = useStyles()
  return (
    <figure className={c.figure} aria-labelledby={`${id}-title`}>
      <div className={c.title} id={`${id}-title`}>
        {title}
      </div>
      {children}
      <figcaption className={c.caption}>{caption}</figcaption>
    </figure>
  )
}

export function ProcessDiagram({
  steps,
}: {
  steps: { title: string; text: string }[]
}) {
  const c = useStyles()
  return (
    <ol className={c.flow}>
      {steps.map((step, i) => (
        <li key={step.title}>
          <div className={c.step}>
            <span className={c.badge}>Step {i + 1}</span>
            <strong>{step.title}</strong>
            <p>{step.text}</p>
          </div>
          {i < steps.length - 1 && (
            <span className={c.arrow} aria-hidden="true">
              →
            </span>
          )}
        </li>
      ))}
    </ol>
  )
}

export function ComparisonDiagram({
  left,
  right,
}: {
  left: React.ReactNode
  right: React.ReactNode
}) {
  const c = useStyles()
  return (
    <div className={c.columns}>
      <div className={c.panel}>{left}</div>
      <div className={c.panel}>{right}</div>
    </div>
  )
}

function WorkflowFigure({ id }: { id: string }) {
  const workflow = workflows[id]
  return (
    <DiagramFigure id={id} title={workflow.title} caption={workflow.caption}>
      <WorkflowDiagram id={id} />
    </DiagramFigure>
  )
}

export function CalendarDiagram({ slug }: { slug: string }) {
  const c = useStyles()
  switch (slug) {
    case "generate-calendar-events-ai":
      return <WorkflowFigure id="ai-review" />
    case "publish-share-calendar":
      return <WorkflowFigure id="publish-review" />
    case "export-apple-google-calendar":
      return (
        <DiagramFigure
          id="export-diagram"
          title="Choose a file snapshot or a shared link"
          caption="For AI-generated events, select Add to calendar before exporting. The file includes all saved events in the chosen calendar, not just the visible week."
        >
          <ComparisonDiagram
            left={
              <>
                <span className={c.badge}>File export</span>
                <strong>Saved events → .ics file → import</strong>
                <p>
                  Import the same file format into{" "}
                  <strong>Apple Calendar or Google Calendar</strong>.
                </p>
                <div className={c.note}>
                  A snapshot at export time. Later School Calendar edits do not
                  automatically update imported events.
                </div>
                <p>
                  When plans change, export again and review existing imported
                  events before reimporting to avoid duplicates.
                </p>
              </>
            }
            right={
              <>
                <span className={c.badge}>Published link</span>
                <strong>Original → publish → read-only URL</strong>
                <p>Families view the calendar in their browser.</p>
                <div className={c.note}>
                  Edit → republish → refresh the same URL to see the new
                  published version.
                </div>
                <p>
                  This is separate from importing a file into Apple or Google
                  Calendar.
                </p>
              </>
            }
          />
        </DiagramFigure>
      )
    case "move-calendar-events-with-chat": {
      const events = [
        ["Reading", "9:00–9:30 am", "10:00–10:30 am", "30 min"],
        ["Maths", "10:00–10:45 am", "11:00–11:45 am", "45 min"],
        ["Library", "11:00–11:30 am", "12:00–12:30 pm", "30 min"],
      ]
      const panel = (after: boolean) => (
        <>
          <span className={c.badge}>
            {after ? "After confirmation · Monday" : "Before · Monday"}
          </span>
          <strong>
            {after
              ? "Every event starts 1 hour later"
              : "The original practice schedule"}
          </strong>
          {events.map(e => (
            <div className={c.event} key={e[0]}>
              <strong>{e[0]}</strong>
              <br />
              {e[after ? 2 : 1]} · {e[3]}
            </div>
          ))}
        </>
      )
      return (
        <DiagramFigure
          id="move-diagram"
          title="Move the time, keep the duration"
          caption="This example moves all three events. “All schedules” includes events outside the visible week. Review the selected events and check for overlaps before and after confirming."
        >
          <ComparisonDiagram left={panel(false)} right={panel(true)} />
          <WorkflowDiagram id="move-review" />
          <div className={c.note}>
            Request → review event selection → <strong>Confirm move</strong> →
            inspect the result. Republish separately if you share the calendar.
          </div>
        </DiagramFigure>
      )
    }
    case "lock-calendar-weekly-view": {
      const panel = (locked: boolean) => (
        <>
          <span className={c.badge}>
            {locked
              ? "Weekly setting on · desktop"
              : "Weekly setting off · desktop"}
          </span>
          <strong>
            {locked
              ? "Weekday names; navigation hidden"
              : "Dates and week navigation visible"}
          </strong>
          {!locked && <p>← Previous week · Next week →</p>}
          <div className={c.week}>
            {["Mon", "Tue", "Wed"].map((day, i) => (
              <div className={c.day} key={day}>
                <strong>
                  {day}
                  {!locked && (
                    <>
                      <br />
                      {28 + i} Sep
                    </>
                  )}
                </strong>
                <p>{["Reading", "Maths", "Sport"][i]}</p>
              </div>
            ))}
          </div>
          <p>
            {locked
              ? "The selected timetable stays in view. You can still edit your events."
              : "Navigate to the week you want before enabling the setting."}
          </p>
        </>
      )
      return (
        <DiagramFigure
          id="weekly-view-diagram"
          title="A display setting, not repeating events"
          caption="Simplified desktop illustration, showing three weekdays. On phones the editor uses a day view and shared calendars use an agenda view; the setting does not force a seven-day grid."
        >
          <ComparisonDiagram left={panel(false)} right={panel(true)} />
          <div className={c.note}>
            The same saved events remain. No new events are copied into future
            weeks. Republish to update a shared calendar’s display.
          </div>
        </DiagramFigure>
      )
    }
    default:
      return null
  }
}

function RectangleExample({ area }: { area: boolean }) {
  const c = useStyles()
  return (
    <div className={c.panel}>
      <strong>
        {area ? "Cover the surface → area" : "Go around the edge → perimeter"}
      </strong>
      <svg className={c.svg} viewBox="0 0 260 180" aria-hidden="true">
        <rect
          x="48"
          y="35"
          width="168"
          height="112"
          fill={area ? "#dbeafe" : "white"}
          stroke={area ? "#46648a" : "#b37625"}
          strokeWidth={area ? 2 : 6}
        />
        {area && (
          <path
            d="M76 35v112M104 35v112M132 35v112M160 35v112M188 35v112M48 63h168M48 91h168M48 119h168"
            stroke="#8aaace"
            fill="none"
          />
        )}
        <text x="132" y="23" textAnchor="middle" fontSize="16" fill="#233348">
          6 cm
        </text>
        <text
          x="25"
          y="98"
          textAnchor="middle"
          fontSize="16"
          fill="#233348"
          transform="rotate(-90 25 98)"
        >
          4 cm
        </text>
      </svg>
      <p>
        {area ? "6 × 4 = 24 cm² of paper" : "2 × (6 + 4) = 20 cm of ribbon"}
      </p>
    </div>
  )
}

export function StudyDiagram({ id }: { id: string }) {
  const c = useStyles()
  switch (id) {
    case "leitner":
      return <WorkflowFigure id="leitner-routing" />
    case "error-log":
      return <WorkflowFigure id="error-log-loop" />
    case "regulation":
      return <WorkflowFigure id="plan-monitor-loop" />
    case "feynman":
      return <WorkflowFigure id="feynman-loop" />
    case "weekly-review":
      return <WorkflowFigure id="weekly-review-loop" />
    case "cornell":
      return (
        <DiagramFigure
          id="cornell-diagram"
          title="A Cornell page you can study from"
          caption="Cover the notes on the right, answer the cues on the left, then uncover and check. Return to the cues on another day. The questions should prompt answers, not duplicate the notes."
        >
          <div className={c.notes}>
            <div className={c.notesTop}>
              <div className={c.cue}>
                <strong>Cues / questions</strong>
                <p>What happens when ice melts?</p>
                <p>Which change reverses melting?</p>
                <p>Why do droplets form outside a cold glass?</p>
              </div>
              <div className={c.notesBody}>
                <strong>Lesson notes: changes of state</strong>
                <p>
                  <strong>Melting:</strong> solid → liquid.
                </p>
                <p>
                  <strong>Freezing:</strong> liquid → solid.
                </p>
                <p>
                  <strong>Evaporation:</strong> liquid → gas.
                </p>
                <p>
                  <strong>Condensation:</strong> gas → liquid.
                </p>
                <p>
                  Water vapour in the surrounding air cools and condenses on the
                  cold glass. The water is not leaking through it.
                </p>
              </div>
            </div>
            <div className={c.summary}>
              <strong>Summary</strong>
              <p>
                Water can change state. The names describe the direction of
                change.
              </p>
            </div>
          </div>
        </DiagramFigure>
      )
    case "interleaving": {
      const tiles = (mixed: boolean) => (
        <>
          <span className={c.badge}>
            {mixed ? "Mixed practice" : "Blocked practice"}
          </span>
          <strong>
            {mixed
              ? "Choose the method each time"
              : "Practise one type at a time"}
          </strong>
          <div className={c.tiles}>
            {(mixed
              ? ["P", "A", "P", "A", "A", "P"]
              : ["P", "P", "P", "A", "A", "A"]
            ).map((label, i) => (
              <span
                className={`${c.tile} ${label === "A" ? c.secondTile : ""}`}
                key={i}
              >
                {label}
              </span>
            ))}
          </div>
          <p>P = perimeter question; A = area question.</p>
        </>
      )
      return (
        <DiagramFigure
          id="interleaving-diagram"
          title="Same question types, different practice order"
          caption="Learn unfamiliar types with support first, then mix related questions. The letters explain the practice order; real questions should not reveal the method in their headings. Mixing practice is not switching between unrelated distractions."
        >
          <ComparisonDiagram left={tiles(false)} right={tiles(true)} />
          <div className={c.columns} style={{ marginTop: 16 }}>
            <RectangleExample area={false} />
            <RectangleExample area />
          </div>
        </DiagramFigure>
      )
    }
    case "visuals":
      return (
        <DiagramFigure
          id="water-diagram"
          title="Follow water through the model"
          caption="A simplified route, not the whole water cycle. Water can take other pathways, including runoff, infiltration and transpiration. Rebuild this model from the process names, then check the arrow meanings."
        >
          <svg
            viewBox="0 0 360 240"
            style={{
              display: "block",
              width: "100%",
              maxWidth: 480,
              margin: "0 auto 20px",
            }}
            aria-hidden="true"
          >
            <ellipse
              cx="80"
              cy="190"
              rx="57"
              ry="17"
              fill="#b8ddf3"
              stroke="#346a98"
              strokeWidth="3"
            />
            <path
              d="M65 147V74m-9 12 9-12 9 12"
              fill="none"
              stroke="#346a98"
              strokeWidth="3"
            />
            <text x="79" y="111" fontSize="16" fill="#233348">
              1
            </text>
            <text x="21" y="224" fontSize="17" fill="#233348">
              Liquid water
            </text>
            <text x="20" y="51" fontSize="17" fill="#233348">
              Water vapour
            </text>
            <path
              d="M141 49h67m-10-9 10 9-10 9"
              fill="none"
              stroke="#346a98"
              strokeWidth="3"
            />
            <text x="169" y="32" fontSize="16" fill="#233348">
              2
            </text>
            <path
              d="M240 72c-33 0-35-36-12-42 0-28 45-34 55-9 31-8 49 35 23 49Z"
              fill="#dceaf6"
              stroke="#346a98"
              strokeWidth="3"
            />
            <text x="227" y="100" fontSize="17" fill="#233348">
              Cloud droplets
            </text>
            <path
              d="M265 121v56m-9-10 9 10 9-10M241 126l-7 14m54-14-7 14M242 194H151m10-9-10 9 10 9"
              fill="none"
              stroke="#346a98"
              strokeWidth="3"
            />
            <text x="281" y="158" fontSize="16" fill="#233348">
              3
            </text>
            <text x="196" y="224" fontSize="17" fill="#233348">
              Rain to the ground
            </text>
          </svg>
          <ProcessDiagram
            steps={[
              {
                title: "Liquid water in a puddle",
                text:
                  "Evaporation: some liquid water changes into water vapour and enters the air.",
              },
              {
                title: "Water vapour in the air",
                text:
                  "Condensation: when water vapour cools, it can change into tiny liquid droplets that form clouds.",
              },
              {
                title: "Droplets in a cloud",
                text:
                  "Precipitation: droplets can grow large enough to fall as rain, returning liquid water to the ground.",
              },
            ]}
          />
          <div className={c.note}>
            Rain → water on the ground → some may evaporate again.{" "}
            <strong>
              The rising arrow represents a change to vapour; it does not mean
              the sun pulls liquid water upward.
            </strong>
          </div>
        </DiagramFigure>
      )
    default:
      return null
  }
}
